/**
 * The info bubble of a calculated Baseline row: its formula, and the cells a user can change
 * to move it - found by following the formula's references, through every formula in between,
 * to the inputs at the bottom. Read off the workbook as computed, so it is never out of date
 * with a fix or a formula; nothing here is written by hand.
 */

import type { MComputed, MRow, MTab } from '../../../store/m-accounting';
import type { RowHint } from '../table/RowHint';
import { sheetLabel } from './mFormulas';

interface Located {
  tab: MTab;
  row: MRow;
  col: string;
  year: number | null;
}

const REF =
  /(?<![A-Za-z0-9_.$])(?:(?:'((?:[^']|'')+)'|([A-Za-z_][A-Za-z0-9_.]*))!)?\$?([A-Z]{1,3})\$?([0-9]+)(?::\$?([A-Z]{1,3})\$?([0-9]+))?(?![A-Za-z0-9_(])/g;

function colNumber(col: string): number {
  let n = 0;
  for (const ch of col) n = n * 26 + (ch.charCodeAt(0) - 64);
  return n;
}

function colLetters(n: number): string {
  let out = '';
  while (n > 0) {
    const r = (n - 1) % 26;
    out = String.fromCharCode(65 + r) + out;
    n = Math.floor((n - 1) / 26);
  }
  return out;
}

const key = (sheet: string, col: string, row: number) => `${sheet.trim()}!${col}${row}`;

/** Every cell a formula references, as keys - string literals ignored, ranges expanded. */
export function references(formula: string, ownSheet: string): string[] {
  const text = formula.replace(/"[^"]*"/g, '""');
  const out: string[] = [];
  for (const m of text.matchAll(REF)) {
    const sheet = m[1] !== undefined ? m[1].replace(/''/g, "'") : (m[2] ?? ownSheet);
    const c1 = colNumber(m[3]);
    const r1 = Number(m[4]);
    const c2 = m[5] ? colNumber(m[5]) : c1;
    const r2 = m[6] ? Number(m[6]) : r1;
    for (let c = Math.min(c1, c2); c <= Math.max(c1, c2); c++) {
      for (let r = Math.min(r1, r2); r <= Math.max(r1, r2); r++) out.push(key(sheet, colLetters(c), r));
    }
  }
  return out;
}

/** 2023, 2024, 2025 -> "2023-2025"; gaps listed: "2023, 2025". */
export function yearSpan(years: number[]): string {
  const sorted = [...years].sort((a, b) => a - b);
  const runs: string[] = [];
  for (let i = 0; i < sorted.length; ) {
    let j = i;
    while (j + 1 < sorted.length && sorted[j + 1] === sorted[j] + 1) j++;
    runs.push(j > i ? `${sorted[i]}–${sorted[j]}` : String(sorted[i]));
    i = j + 1;
  }
  return runs.join(', ');
}

export class BaselineHints {
  private cells = new Map<string, Located>();
  private sources = new Map<string, Set<string>>();
  private rowHints = new Map<string, RowHint | null>();
  /** A row's name as a reader finds it: under its heading where the label repeats on the tab. */
  private names = new Map<MRow, string>();

  constructor(computed: MComputed) {
    for (const tab of computed.tabs) {
      const counts = new Map<string, number>();
      for (const row of tab.rows) counts.set(row.label, (counts.get(row.label) ?? 0) + 1);
      let heading = '';
      for (const row of tab.rows) {
        if (Object.keys(row.cells).length === 0) heading = row.label.replace(/:$/, '').trim();
        const label = row.label || `row ${row.row}`;
        this.names.set(row, (counts.get(row.label) ?? 0) > 1 && heading ? `${heading} › ${label}` : label);
      }
      for (const row of tab.rows) {
        for (const { col, year } of tab.columns) {
          if (row.cells[col]) this.cells.set(key(tab.sheet, col, row.row), { tab, row, col, year });
        }
      }
    }
  }

  /** The cells at the bottom of a cell's formulas: inputs, and fixed values nothing edits. */
  private sourcesOf(cellKey: string, visiting = new Set<string>()): Set<string> {
    const known = this.sources.get(cellKey);
    if (known) return known;
    const found = new Set<string>();
    const at = this.cells.get(cellKey);
    if (!at || visiting.has(cellKey)) return found;
    const cell = at.row.cells[at.col];
    if (cell.input || !cell.formula) {
      found.add(cellKey);
    } else {
      visiting.add(cellKey);
      for (const ref of references(cell.formula.replace(/^=/, ''), at.tab.sheet)) {
        for (const s of this.sourcesOf(ref, visiting)) found.add(s);
      }
      visiting.delete(cellKey);
    }
    this.sources.set(cellKey, found);
    return found;
  }

  /** The bubble for a row, or null where every cell in it can be typed in. */
  forRow(tab: MTab, row: MRow): RowHint | null {
    const rowKey = `${tab.sheet}|${row.row}`;
    if (this.rowHints.has(rowKey)) return this.rowHints.get(rowKey)!;
    const hint = this.build(tab, row);
    this.rowHints.set(rowKey, hint);
    return hint;
  }

  private build(tab: MTab, row: MRow): RowHint | null {
    const locked = tab.columns.filter(({ col }) => {
      const cell = row.cells[col];
      return cell && !cell.input && cell.value !== null && cell.value !== '';
    });
    if (locked.length === 0) return null;
    const calculated = locked.filter(({ col }) => row.cells[col].formula);
    if (calculated.length === 0) {
      return {
        how: 'A fixed value in the workbook that no calculation reads.',
        change: 'Nothing to change: editing it would move no figure, so it is not editable.',
      };
    }

    const sample = calculated.find(({ year }) => year !== null) ?? calculated[0];
    const sampleCell = row.cells[sample.col];
    const how = `${sampleCell.formula}${sample.year !== null ? ` (${sample.year}; the other years are the same formula one column on)` : ''}${sampleCell.fix ? ` - fix ${sampleCell.fix}` : ''}`;

    // Group the sources by the row they sit on, with the years each covers.
    const groups = new Map<string, { label: string; years: Set<number>; editable: boolean }>();
    for (const { col } of calculated) {
      for (const s of this.sourcesOf(key(tab.sheet, col, row.row))) {
        const at = this.cells.get(s)!;
        if (at.tab === tab && at.row === row) continue;
        const editable = at.row.cells[at.col].input;
        const label = `${sheetLabel(at.tab.name)} › ${this.names.get(at.row)}`;
        const g = groups.get(label) ?? { label, years: new Set<number>(), editable };
        if (at.year !== null) g.years.add(at.year);
        g.editable ||= editable;
        groups.set(label, g);
      }
    }
    const describe = (g: { label: string; years: Set<number> }) =>
      g.years.size ? `${g.label} (${yearSpan([...g.years])})` : g.label;
    const editable = [...groups.values()].filter((g) => g.editable);
    const fixed = [...groups.values()].filter((g) => !g.editable);
    const shown = editable.slice(0, 8).map(describe);
    if (editable.length > 8) shown.push(`and ${editable.length - 8} more`);
    let change = shown.length ? `The inputs it is built from: ${shown.join('; ')}.` : 'It reads no input.';
    if (fixed.length) {
      change += ` It also reads fixed values that are not edited here: ${fixed.slice(0, 4).map(describe).join('; ')}${fixed.length > 4 ? '; …' : ''}.`;
    }
    return { how, change };
  }
}
